
import { DashboardAPI } from "@/services/api/Dashboard.service";
import {
  defineComponent,
  ref,
  onMounted,
  onUnmounted,
  inject,
  computed,
  watch,
} from "vue";
import Datatable from "@/components/kt-datatable/KTDatatable.vue";
import { FormatMethods } from "@/helpers/data-format";
import { INNER_PAGE_ROWS_PER_PAGE, usePagination } from "@/helpers/pagination";
import {
  siteTableFilters,
  siteTableHeaders,
} from "@/views/site/components/siteDashboardConfig";
import { IDashboardSitesDetail } from "@/interfaces/iDashboard";
import { ISite } from "@/interfaces/iSite";
import { SiteApi } from "@/services/api/Site.service";
import SiteDetailModal from "@/views/site/modal/SiteDetailModal.vue";
import ViewSiteDetailModal from "@/views/site/modal/ViewSiteDetailModal.vue";
import LineChartModal from "@/components/modals/chart/LineChartModal.vue";
import { getLookups } from "@/services/api/Lookup.service";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { TranslationService } from "@/core/services/TranslationService";

import {
  hasPermission,
  hasPermissionWrapper,
} from "@/core/services/permission.service";
import { GET_SELECTED_SITES_STR, SITE_STORE } from "@/store/userSite/types";
import store from "@/store";
import {
  IDashboardSiteSeries,
  IDashboardSiteCounters,
  IDashboardSiteChartData,
  IDashboardSiteSingleBarData,
} from "@/interfaces/iDashboard";
import { dashboardChartOptions } from "@/views/site/components/siteDashboardConfig";
import { Emitter } from "mitt";
import moment from "moment-timezone";
import { useRouter } from "vue-router";
import { VZ_PERMISSIONS } from "@/helpers/permissionMapper";
type DateRangeEmitter = {
  VZDateRange: string[];
};
type refreshDashboard = {
  refreshDashboard: boolean;
};
export default defineComponent({
  name: "branch-dashboard",
  components: {
    Datatable,
    SiteDetailModal,
    ViewSiteDetailModal,
    LineChartModal,
  },
  setup() {
    const t = (key: string) => {
      return TranslationService.translate(key);
    };
    const emitter: Emitter<DateRangeEmitter> | undefined = inject("emitter");
    const emit: Emitter<refreshDashboard> | undefined = inject("emitter");
    const dateFilterValue = ref({
      startDate: moment()
        .subtract(6, "days")
        .startOf("day")
        .format("YYYY-MM-DDTHH:mm:ssZ"),
      endDate: moment(moment(), "YYYY-MM-DDTHH:mm:ssZ").endOf("day").format(),
    });
    const series = ref<Array<IDashboardSiteSeries>>([]);
    const chartOptions = ref(dashboardChartOptions());
    const siteCount = ref<number>();
    const formCount = ref<number>();
    const userCount = ref<number>();
    const deviceCount = ref<number>();
    const ChartData = ref<Array<IDashboardSiteChartData>>([]);
    const allCategoriesForChart = ref<Array<string>>([]);
    const allTypesForChart = ref<Array<string>>([]);
    const singleBarData = ref<Array<IDashboardSiteSingleBarData>>([]);
    const categoryLookups = ref<Array<any>>([]);
    const autorefresh = ref<boolean>(false);
    const router = useRouter();

    const { page, total } = usePagination();
    const rowsPerPage = ref(INNER_PAGE_ROWS_PER_PAGE);
    const loading = ref(false);
    const defaultFilter = ref({ status: "ACTIVE" });
    const showSearch = true;
    const tableData = ref<IDashboardSitesDetail[]>([]);
    const tableHeader = ref(siteTableHeaders());
    const tableFilter = ref(siteTableFilters());
    const showDetailDialog = ref<any>(false);
    const showViewDetailDialog = ref<any>(false);
    const showChartDialog = ref<any>(false);
    const localPreference = ref({});
    const selectedSite = ref<Partial<ISite>>({});
    const viewSelectedSite = ref({});
    const format = computed(() => {
      return FormatMethods;
    });
    const tableRowClick = (item: any) => {
      // TODO: For actions
    };

    const openDetailModal = async (modalType: string, site: any) => {
      selectedSite.value = {};
      const siteObj = await SiteApi.getInstance().getSiteById(site.webId);
      selectedSite.value = siteObj;
      if (modalType == "chart") {
        showChartDialog.value = true;
      } else {
        showDetailDialog.value = true;
      }
    };
    const openViewModal = async (site: any) => {
      viewSelectedSite.value = {};
      const siteObj = await SiteApi.getInstance().getSiteById(site.webId);
      viewSelectedSite.value = siteObj;
      showViewDetailDialog.value = true;
    };
    const queryUpdate = async (params?: Record<string, number | string>) => {
      try {
        loading.value = true;
        localPreference.value = { ...params };
        const data = await DashboardAPI.getInstance().getSitesDetail({
          ...params,
          startDate: moment(dateFilterValue.value?.startDate)
            .startOf("day")
            .format("YYYY-MM-DDTHH:mm:ssZ")
            .toString(),
          endDate: moment(dateFilterValue.value?.endDate)
            .endOf("day")
            .format("YYYY-MM-DDTHH:mm:ssZ"),
        });
        tableData.value = [...data.content];
        total.value = data.totalElements;
        rowsPerPage.value = data.size;
        page.value = ++data.number;
      } catch (err) {
        console.error(err);
      } finally {
        loading.value = false;
      }
    };
    const refreshList = () => {
      queryUpdate({ ...localPreference.value });
    };
    watch(
      dateFilterValue,
      () => {
        queryUpdate({
          ...defaultFilter.value,
          page: page.value - 1,
          size: rowsPerPage.value,
        });
      },
      { deep: true }
    );

    // Methods
    const setXaxis = async (uniqueXaxisData, allApiData) => {
      ChartData.value = [];
      allCategoriesForChart.value = [];
      chartOptions.value.xaxis.categories = [];
      allTypesForChart.value = [];
      singleBarData.value = [];
      series.value = [];
      allApiData.forEach((allData, indexAll) => {
        allTypesForChart.value.push(allData.type);
      });
      allTypesForChart.value = allTypesForChart.value.filter((c, index) => {
        return allTypesForChart.value.indexOf(c) === index;
      });
      allTypesForChart.value.forEach((allTypes, indexTypes) => {
        let object = {
          name: "",
          data: [],
        };
        // if (allTypes == t("common.on_time")) {
        //   object.name = t("common.onTime");
        // } else if (allTypes == t("common.outside_period")) {
        //   object.name = t("common.outsidePeriod");
        // } else {
        //   object.name = t("common.outsidePeriod");
        // }
        if (allTypes == "ON_TIME") {
          object.name = "On Time";
        } else if (allTypes == "OUTSIDE_PERIOD") {
          object.name = "Outside Period";
        } else {
          object.name = "Expired";
        }
        singleBarData.value.push(object);
      });
      series.value = singleBarData.value;

      uniqueXaxisData.forEach((uniqueData, indexUnique) => {
        allApiData.forEach((allData, indexAll) => {
          if (uniqueData == allData.categoryCodeText) {
            allTypesForChart.value.forEach((singleUniqueType, indexTypes) => {
              if (allData.type == singleUniqueType) {
                series.value[indexTypes].data.push(allData.count);
              }
            });
          }
        });
      });
    };
    const getSiteChartData = async () => {
      try {
        // store.dispatch(Actions.ADD_BODY_CLASSNAME, "page-loading");
        await getCategoryLookup();
        const response = await DashboardAPI.getInstance().getSiteChartData(
          moment(dateFilterValue.value.startDate)
            .startOf("day")
            .format("YYYY-MM-DDTHH:mm:ssZ")
            .toString(),
          moment(dateFilterValue.value.endDate)
            .endOf("day")
            .format("YYYY-MM-DDTHH:mm:ssZ")
            .toString()
        );
        ChartData.value = response;

        // Build unique categories from lookups (filter empty)
        allCategoriesForChart.value = categoryLookups.value
          .map((data) => data.text || "")
          .filter((text) => text !== "");
        if (allCategoriesForChart.value[0] === "") {
          allCategoriesForChart.value.shift();
        }

        allTypesForChart.value = Array.from(
          new Set(response.map((item) => item.type))
        );

        // Normalize into matrix: category → type → count
        const normalized = {};
        allCategoriesForChart.value.forEach((cat) => {
          normalized[cat] = {};
          allTypesForChart.value.forEach((type) => {
            normalized[cat][type] = 0; // default to 0
          });
        });

        response.forEach((item) => {
          if (allCategoriesForChart.value.includes(item.categoryCodeText)) {
            normalized[item.categoryCodeText][item.type] = item.count;
          }
        });

        series.value = allTypesForChart.value.map((type) => ({
          name:
            type === "ON_TIME"
              ? "On Time"
              : type === "OUTSIDE_PERIOD"
              ? "Outside Period"
              : "Expired",
          data: allCategoriesForChart.value.map((cat) => normalized[cat][type]),
        }));

        chartOptions.value = {
          ...chartOptions.value,
          xaxis: {
            ...chartOptions.value.xaxis,
            categories: allCategoriesForChart.value,
          },
        };

        window.dispatchEvent(new Event("resize"));
      } catch (error) {
        console.log(error);
      } finally {
        // store.dispatch(Actions.REMOVE_BODY_CLASSNAME, "page-loading");
      }
    };
    const getSiteCounters = async () => {
      await DashboardAPI.getInstance()
        .getSiteCounters()
        .then((response) => {
          siteCount.value = response.siteCount;
          formCount.value = response.formCount;
          deviceCount.value = response.deviceCount;
          userCount.value = response.userCount;
        })
        .catch(() => {
          //
        });
    };
    const getCategoryLookup = () => {
      return new Promise((resolve, reject) => {
        getLookups(
          "static-data/filter/LOOKUP_FILTER/filters?filter[lookupName]=LKFormsCategory"
        )
          .then((res) => {
            categoryLookups.value = res;
            resolve(res);
          })
          .catch((err) => {
            reject(err);
          });
      });
    };
    const selectedSites = computed(() => {
      const key = `${SITE_STORE}/${GET_SELECTED_SITES_STR}`;
      return store.getters[key] as string;
    });

    const checkPermission = (permissions, page) => {
      if (hasPermission(permissions)) {
        router.push({ name: page });
      }
    };

    // const getPageData = async (value) => {
    //   getSiteChartData();
    // };
    let intervalId: any;
    onMounted(() => {
      setCurrentPageBreadcrumbs(t("common.branchesDashboard"), [
        t("common.home"),
        t("common.branchesDashboard"),
      ]);
      loading.value = true;
      callMethods(); // Initial call
      intervalId = setInterval(callMethods, 900000); // 900000 ms = 15 minutes
    });

    onUnmounted(() => {
      clearInterval(intervalId); // clear main dashboard interval
      emitter?.off("VZDateRange");
      emit?.off("refreshDashboard");
    });
    const callMethods = () => {
      getSiteCounters();
      getSiteChartData();
      emitter?.on("VZDateRange", (value) => {
        dateFilterValue.value.startDate = value[0];
        dateFilterValue.value.endDate = value[1];
        ChartData.value = [];
        allCategoriesForChart.value = [];
        chartOptions.value.xaxis.categories = [];
        allTypesForChart.value = [];
        singleBarData.value = [];
        series.value = [];
        getSiteChartData();
      });
      emit?.on("refreshDashboard", (value) => {
        loading.value = true;
        ChartData.value = [];
        allCategoriesForChart.value = [];
        chartOptions.value.xaxis.categories = [];
        allTypesForChart.value = [];
        singleBarData.value = [];
        series.value = [];
        getSiteCounters();
        getSiteChartData();
        queryUpdate({ ...localPreference.value });
      });
    };
    watch(selectedSites, () => {
      getSiteCounters();
      callMethods();
    });
    return {
      t,
      siteCount,
      formCount,
      userCount,
      deviceCount,
      setXaxis,
      chartOptions,
      series,
      allCategoriesForChart,
      allTypesForChart,
      dateFilterValue,
      // getPageData,
      hasPermission,
      checkPermission,
      hasPermissionWrapper,
      VZ_PERMISSIONS,
      autorefresh,
      format,
      tableData,
      tableHeader,
      tableFilter,
      showSearch,
      page,
      total,
      rowsPerPage,
      defaultFilter,
      loading,
      tableRowClick,
      queryUpdate,
      refreshList,
      openDetailModal,
      selectedSite,
      showDetailDialog,
      showViewDetailDialog,
      showChartDialog,
      viewSelectedSite,
      openViewModal,
    };
  },
});
